import React, { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { PHONE, PHONE_TEL, CONTACT_EMAIL, buildWhatsAppUrl } from "@/lib/site";
import { submitContact } from "@/lib/api";
import { useAntiBot } from "@/lib/useAntiBot";
import { Phone, MessageCircle, MapPin, Clock, Mail, Check, AlertCircle } from "lucide-react";
import MapSection from "@/components/MapSection";

const Contacto = () => {
  const { t, lang } = useLang();
  const antibot = useAntiBot();
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    treatment: "",
    consent: false,
  });

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ state: "error", msg: t.contact.f_required });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await submitContact({
        name: form.name,
        phone: form.phone,
        email: form.email,
        message: form.message,
        treatment: form.treatment || null,
        consent: form.consent,
        source: "contact",
        locale: lang,
        hp: antibot.hp,
        t: antibot.startedAt,
      });
      setStatus({ state: "success", msg: t.contact.f_success });
      setForm({ name: "", phone: "", email: "", message: "", treatment: "", consent: false });
    } catch (err) {
      setStatus({ state: "error", msg: t.contact.f_error });
    }
  };

  return (
    <main data-testid="contact-page" className="bg-cream text-ink">
      {/* HERO */}
      <section className="pt-40 pb-12 md:pt-48 md:pb-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="overline">{t.contact.eyebrow}</p>
          <h1 className="mt-6 font-serif text-6xl md:text-8xl font-light tracking-tight leading-[0.95] max-w-[14ch]">{t.contact.title}</h1>
          <p className="mt-8 max-w-2xl text-ink/75 font-light text-lg leading-relaxed">{t.contact.lede}</p>
        </div>
      </section>

      {/* CTAs grandes */}
      <section className="pb-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-6">
          <a
            href={buildWhatsAppUrl("contact", lang)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-cta"
            className="group border border-ink p-10 flex flex-col gap-4 hover:bg-ink hover:text-cream transition-colors duration-500"
          >
            <MessageCircle size={28} strokeWidth={1.2} />
            <p className="overline opacity-70">WhatsApp directo</p>
            <p className="font-serif text-3xl md:text-4xl font-light">{t.contact.whatsapp}</p>
            <span className="mt-2 text-xs tracking-[0.22em] uppercase opacity-70 group-hover:opacity-100">
              621 078 618 →
            </span>
          </a>

          <a
            href={PHONE_TEL}
            data-testid="contact-call-cta"
            className="group border border-ink p-10 flex flex-col gap-4 hover:bg-ink hover:text-cream transition-colors duration-500"
          >
            <Phone size={28} strokeWidth={1.2} />
            <p className="overline opacity-70">Línea directa</p>
            <p className="font-serif text-3xl md:text-4xl font-light">{PHONE}</p>
            <span className="mt-2 text-xs tracking-[0.22em] uppercase opacity-70 group-hover:opacity-100">
              {t.contact.call} →
            </span>
          </a>
        </div>
      </section>

      {/* INFO + FORM */}
      <section className="pb-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5 space-y-10">
            <InfoBlock icon={<MapPin size={18} strokeWidth={1.5} />} label={t.contact.address_label} value={t.contact.address} />
            <InfoBlock icon={<Clock size={18} strokeWidth={1.5} />} label={t.contact.hours_label} value={t.contact.hours} />
            <InfoBlock icon={<Mail size={18} strokeWidth={1.5} />} label={t.contact.email_label} value={CONTACT_EMAIL} href={`mailto:${CONTACT_EMAIL}`} />
          </div>

          <div className="md:col-span-7 md:pl-12">
            <p className="overline">{t.contact.form_title}</p>
            <form onSubmit={onSubmit} data-testid="contact-form" className="mt-8 space-y-7">
              {/* Honeypot anti-bot (invisible for humans) */}
              <input {...antibot.honeypotProps} />

              <Field name="name" label={t.contact.f_name} value={form.name} onChange={(v) => onChange("name", v)} />
              <div className="grid md:grid-cols-2 gap-8">
                <Field name="phone" label={t.contact.f_phone} type="tel" value={form.phone} onChange={(v) => onChange("phone", v)} />
                <Field name="email" label={t.contact.f_email} type="email" value={form.email} onChange={(v) => onChange("email", v)} />
              </div>
              <Field name="treatment" label={t.contact.f_treatment} required={false} value={form.treatment} onChange={(v) => onChange("treatment", v)} />
              <Field name="message" label={t.contact.f_message} multiline value={form.message} onChange={(v) => onChange("message", v)} />
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => onChange("consent", e.target.checked)}
                  data-testid="form-field-consent"
                  className="mt-1 w-4 h-4 border-ink"
                  required
                />
                <span className="text-sm font-light text-ink/75">{t.contact.f_consent}</span>
              </label>
              <button
                type="submit"
                data-testid="contact-form-submit"
                disabled={status.state === "loading"}
                className="px-8 py-4 bg-ink text-cream text-xs tracking-[0.22em] uppercase btn-sweep sweep-cream border border-ink hover:text-ink disabled:opacity-60"
              >
                {status.state === "loading" ? "..." : `${t.contact.f_submit} →`}
              </button>
              {status.state === "success" && (
                <div data-testid="contact-form-success" className="flex items-center gap-3 text-sm font-light text-ink/85">
                  <Check size={16} /> {status.msg}
                </div>
              )}
              {status.state === "error" && (
                <div data-testid="contact-form-error" className="flex items-center gap-3 text-sm font-light text-red-700">
                  <AlertCircle size={16} /> {status.msg}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* MAP */}
      <MapSection variant="light" />
    </main>
  );
};

const InfoBlock = ({ icon, label, value, href }) => {
  const inner = (
    <>
      <div className="flex items-center gap-3 overline">
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-3 font-serif text-2xl md:text-3xl font-light whitespace-pre-line leading-snug">{value}</p>
    </>
  );
  return (
    <div className="border-t border-ink/15 pt-6">
      {href ? <a href={href} className="link-underline block">{inner}</a> : inner}
    </div>
  );
};

const Field = ({ name, label, type = "text", multiline = false, value, onChange, required = true }) => {
  return (
    <label className="block group">
      <span className="overline block mb-3 text-ink/60 group-focus-within:text-ink transition-colors">{label}</span>
      {multiline ? (
        <textarea
          name={name}
          data-testid={`form-field-${name}`}
          required={required}
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-2 font-light text-lg resize-none transition-colors"
        />
      ) : (
        <input
          type={type}
          name={name}
          data-testid={`form-field-${name}`}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-2 font-light text-lg transition-colors"
        />
      )}
    </label>
  );
};

export default Contacto;
